import type { NotificationConditionType } from '@cims-online/shared'
import type { ConditionFormValue } from '../types'
import {
  createNotificationReferenceValues,
  labeledValueString,
  labeledValueStrings,
  toNotificationStatusReferences,
  type NotificationReferenceData,
} from '../../../utils/referenceValues'

type StatusIncludesCondition = Extract<
  NotificationConditionType,
  { type: 'status_includes' }
>
type PersistedStatus = StatusIncludesCondition['statuses'][number]

/**
 * Copies persisted conditions into independent form drafts while preserving
 * their evaluation order.
 */
export function toFormValues(
  conditions: NotificationConditionType[],
  referenceData: NotificationReferenceData
): ConditionFormValue[] {
  const references = createNotificationReferenceValues(referenceData)

  return conditions.map((condition) => {
    if (condition.type === 'includes_options')
      return {
        type: condition.type,
        field: references.field(condition.fieldId),
        options: condition.optionIds.map((optionId) =>
          references.option(condition.fieldId, optionId)
        ),
      }

    if (
      condition.type === 'all_option_states_filled' ||
      condition.type === 'any_option_state_empty'
    )
      return {
        type: condition.type,
        field: references.field(condition.fieldId),
      }

    return {
      type: condition.type,
      statuses: condition.statuses.map(references.state),
    }
  })
}

/**
 * Converts condition drafts to the persisted union shape in their current
 * list order. Incomplete drafts return `undefined` and are expected to be
 * reported by the form's visible validation controls.
 */
export function toSubmission(
  drafts: ConditionFormValue[]
): NotificationConditionType[] | undefined {
  const conditions: NotificationConditionType[] = []

  for (const draft of drafts) {
    const fieldId = labeledValueString(draft.field)
    const optionIds = labeledValueStrings(draft.options)

    if (draft.type === 'includes_options' && fieldId && optionIds.length) {
      conditions.push({
        type: draft.type,
        fieldId,
        optionIds,
      })
    } else if (
      (draft.type === 'all_option_states_filled' ||
        draft.type === 'any_option_state_empty') &&
      fieldId
    ) {
      conditions.push({ type: draft.type, fieldId })
    } else if (draft.type === 'status_includes' && draft.statuses?.length) {
      const references = toNotificationStatusReferences(draft.statuses)
      if (!references) return undefined
      const statuses = references.filter(
        (status): status is PersistedStatus => status.type !== 'create'
      )
      if (statuses.length !== references.length) return undefined
      conditions.push({ type: draft.type, statuses })
    } else {
      return undefined
    }
  }

  return conditions
}
